import { type Lap } from '../../domain/lap/Lap'
import { analyzeTrackZones } from '../zones/TrackZoneAnalysis'
import {
  type ZoneDetectionConfig,
  type ZoneAnalysisResult,
  type TrackZone,
  type ZoneLapData,
} from '../zones/TrackZoneTypes'
import { DEFAULT_ZONE_DETECTION_CONFIG } from '../config/zoneDetection'
import { type ZoneVariancePoint } from './zoneDisplay/referencePointConsistency/consistencyGradient'
import { getDisplayLaps } from './keyLapSelection'

export interface SessionAnalysisOptions {
  excludedLapIds: Set<string>
  config?: Partial<ZoneDetectionConfig>
  /** Comparison-mode session → "S2" tag, keyed by session id (owner
   *  follow-up 2026-08-21). Absent for single-session callers, so their
   *  attribution/set-aside copy stays byte-identical to before. */
  sessionLabels?: Map<string, string>
}

export interface SessionAnalysisResult {
  zones: TrackZone[]
  focusZones: TrackZone[]
  /** Focus overflow — non-solid zones beyond the FOCUS_MAX_ZONES cap (see ZoneAnalysisResult). */
  improveZones: TrackZone[]
  /** Actionable zones that ARE consistent (consistencyPercent.isConsistent === true) — never overlaps focusZones. */
  solidZones: TrackZone[]
  zoneLapData: Map<string, ZoneLapData[]>
  displayLaps: (zoneId: string) => ZoneLapData[]
  /** Full-track score-derived map gradient (spec 2026-08-20); null when the
   *  session can't support it (too few laps / no GPS). */
  trackGradient: ZoneVariancePoint[] | null
}

// ---------------------------------------------------------------------------
// Named analysis steps.
//
// runSessionAnalysis composes these in one pass (web); mobile's hooks call the
// same steps individually so each can memoize on its own dependencies. Every
// guard and default lives here, in exactly one place — the clients only own
// the composition.
// ---------------------------------------------------------------------------

/** Drop the excluded laps from the analysis set. */
export function filterExcludedLaps(
  domainLaps: Lap[],
  excludedLapIds: Set<string> | undefined
): Lap[] {
  if (!excludedLapIds || excludedLapIds.size === 0) return domainLaps
  return domainLaps.filter((lap) => !excludedLapIds.has(lap.id))
}

function emptyZoneResult(): ZoneAnalysisResult {
  return {
    zones: [],
    focusZones: [],
    improveZones: [],
    solidZones: [],
    zoneLapData: new Map<string, ZoneLapData[]>(),
    trackStats: {
      totalZones: 0,
      highVarianceZones: 0,
      overallConsistency: 0,
      totalTimeLossMs: 0,
      trackLength: 0,
    },
    trackGradient: null,
  }
}

/**
 * Zone-detection step: stable boundaries/numbering from ALL domain laps,
 * metrics from the filtered subset. Empty result below 2 filtered laps —
 * and analyzeTrackZones additionally returns empty below
 * config.minSamplesPerZone laps of usable data (default 4; callers that
 * must work with fewer laps, like the comparison map overlays, pass a
 * lower override).
 *
 * allDomainLaps doubles as both the detection-stability laps AND the
 * display-bounds laps (computeZoneDisplayBounds) — the SAME derivation
 * mobile's chart display uses, so the overview %, chart band, and zone
 * detail all agree.
 */
export function detectSessionZones(
  filteredLaps: Lap[],
  allDomainLaps: Lap[],
  overrides?: Partial<ZoneDetectionConfig>,
  excludedLapIds: Set<string> = new Set(),
  /** Comparison-mode session → "S2" tag, keyed by session id (owner
   *  follow-up 2026-08-21) — forwarded to analyzeTrackZones untouched.
   *  Absent for single-session callers (byte-identical copy to before). */
  sessionLabels?: Map<string, string>
): ZoneAnalysisResult {
  if (filteredLaps.length < 2) return emptyZoneResult()
  const config: ZoneDetectionConfig = {
    ...DEFAULT_ZONE_DETECTION_CONFIG,
    ...overrides,
  }
  return analyzeTrackZones(
    filteredLaps,
    config,
    allDomainLaps,
    allDomainLaps,
    excludedLapIds,
    sessionLabels
  )
}

export function runSessionAnalysis(
  domainLaps: Lap[],
  options: SessionAnalysisOptions
): SessionAnalysisResult {
  const { excludedLapIds, config: overrides, sessionLabels } = options

  const filtered = filterExcludedLaps(domainLaps, excludedLapIds)

  const zoneResult = detectSessionZones(
    filtered,
    domainLaps,
    overrides,
    excludedLapIds,
    sessionLabels
  )

  return {
    zones: zoneResult.zones,
    focusZones: zoneResult.focusZones,
    improveZones: zoneResult.improveZones,
    solidZones: zoneResult.solidZones,
    zoneLapData: zoneResult.zoneLapData,
    trackGradient: zoneResult.trackGradient,
    displayLaps: (zoneId: string) => {
      const data = zoneResult.zoneLapData.get(zoneId) ?? []
      const zone =
        zoneResult.focusZones.find((z) => z.id === zoneId) ??
        zoneResult.zones.find((z) => z.id === zoneId)
      const setAsideIds = new Set(
        zone?.consistencyPercent?.setAside?.lapIds ?? []
      )
      return getDisplayLaps(data, zone?.variance.primaryIssue, setAsideIds)
    },
  }
}
